import { useState } from 'react';
import { Alert } from '@/components/States';
import { money, num, date, dateTime } from '@/lib/format';
import { useFormFactor } from '@/lib/formFactor';
import { monthRange } from './api';
import { EPS, basisLabel, documentInvoice, documentStatus, statementStatus } from './status';
import { termsText } from './PartnerList';
import { docTotals } from './DocList';
import type { StatementPayload } from './types';
import s from './StatementDoc.module.css';

/** 未锁账的对账单都打水印（老系统 PDF 只有锁账后才写「已作为结算确认依据」） */
function watermark(status?: string) {
  if (status === 'DRAFT') return '草稿 · 仅供核对';
  if (status === 'CONFIRMED') return '未锁账 · 仅供核对';
  if (status === 'CANCELLED') return '已作废';
  return undefined;
}

/**
 * 明细版 · 给对方看（高保真稿 T8b / T8d）：抬头、对账期间、逐张单据、合计、结算等式、口径、签章栏。
 * 手机上就是文档阅读形态（纸张撑满、按单据分块、不横滑）；iPad / 桌面按 A4 宽度居中。
 * 新快照固化货物行；历史对账单只保留原有的单据级快照。
 */
export function StatementDoc({ d, showGoods = true }: { d: StatementPayload; showGoods?: boolean }) {
  const ff = useFormFactor();
  const [onlyReceived, setOnlyReceived] = useState(false);
  const [kitComponentsOpen, setKitComponentsOpen] = useState(true);
  const scope = d.scope;
  const partnerLabel = scope === 'supplier' ? '供应商' : '客户';
  const [y, m] = d.period.split('-');
  const sum = d.summary;
  const t = docTotals(d.businessDocuments);
  const amounts = sum.billAmount !== undefined;
  const wm = watermark(d.status);
  const missingGoods = d.businessDocuments.filter((doc) => !doc.goods).length;
  const supplierGoods = scope === 'supplier';
  const supplierTablet = supplierGoods && ff === 'tablet';
  const supplierPhone = supplierGoods && ff === 'phone';
  const customerPhone = !supplierGoods && ff === 'phone';
  const customerScreen = !supplierGoods && ff !== 'phone';
  const allGoods = d.businessDocuments.flatMap((doc) => doc.goods || []);
  const bundleGroups = new Set(allGoods.filter((good) => good.name.includes(' · ')).map((good) => good.name.split(' · ')[0]));
  const adjustmentRows = allGoods.filter((good) => good.adjustmentKind || good.amount < 0).length;
  const goodsAmount = allGoods.reduce((total, good) => total + good.amount, 0);
  const freightAmount = allGoods.filter((good) => good.name.includes('运费')).reduce((total, good) => total + good.amount, 0);
  const customerPhoneGoodsAmount = goodsAmount - freightAmount;
  const unreceivedRows = allGoods.filter((good) => !good.adjustmentKind && good.orderedQuantity !== undefined && good.receivedQuantity !== undefined && good.receivedQuantity < good.orderedQuantity).length;
  const receiptBasis = supplierGoods && d.businessDocuments.length > 0 && d.businessDocuments.every((doc) => doc.goods?.some((good) => good.billingBasis === 'RECEIVED') && doc.goods.every((good) => good.receivedQuantity === undefined || good.billingBasis === 'RECEIVED'));
  const periodReceiptBasis = receiptBasis && d.businessDocuments.every((doc) => doc.goods?.every((good) => good.receiptPeriod === d.period));
  const visibleDocuments = onlyReceived && receiptBasis
    ? d.businessDocuments.filter((doc) => doc.goods?.some((good) => (good.receivedQuantity || 0) > 0 || good.adjustmentKind)).map((doc) => ({ ...doc, goods: doc.goods?.filter((good) => (good.receivedQuantity || 0) > 0 || good.adjustmentKind) }))
    : d.businessDocuments;
  const goodsCount = visibleDocuments.reduce((count, doc) => count + (doc.goods?.length || 0), 0);
  const allGoodsCount = allGoods.length;
  const supplierAdjustmentRows = allGoods.filter((good) => good.adjustmentKind === 'REJECTION_CONTRA' || good.adjustmentKind === 'RETURN' || (good.adjustmentKind === undefined && good.amount < 0)).length;
  const receiptFilter = !supplierPhone && receiptBasis && <button type="button" className={s.receiptFilter} aria-pressed={onlyReceived} onClick={() => setOnlyReceived((value) => !value)}>{onlyReceived ? '显示全部货物' : '只看已入库'}</button>;
  return (
    <div className={`${s.reader} ${customerScreen ? s.customerReader : ''} ${supplierTablet ? s.supplierTabletReader : ''}`}>
      <div className={s.tip}>
        {supplierGoods ? (receiptBasis
          ? <Alert tone="danger" title="供应商对账按已入库数量计价">{periodReceiptBasis ? '仅本月入库计入本月应付，跨月入库归入实际入库月份；登记的拒收或退货在发生月份列出冲减。' : '本历史快照按保存时累计已入库量计价。'}未到货不计入应付；保存后的金额不会随采购单变动。</Alert>
          : <Alert tone="warn" title="历史快照保留保存时的金额口径">旧对账单不会自动改写；缺少入库计价标识的货物行不能当作新口径应付。草稿可重新取数生成新快照。</Alert>)
        : <Alert tone="warn" icon="info" title="明细是保存对账单时的快照，不是实时查单据">之后单据被修改，这里都不变；{missingGoods ? '历史快照若没有货物行，只显示保存时的单据金额。' : '货物行加总与各单据金额一致。'}</Alert>}
      </div>
      {supplierTablet && <div className={`${s.supplierKpis} ${amounts ? '' : s.supplierKpisNoAmounts}`} aria-label="供应商对账统计">
        <div><span>采购单</span><b className="tnum">{d.businessDocuments.length}</b></div>
        <div><span>明细行</span><b className="tnum">{allGoodsCount}</b></div>
        <div><span>未到货行</span><b className="tnum">{receiptBasis ? unreceivedRows : '—'}</b></div>
        <div><span>冲减行</span><b className="tnum">{supplierAdjustmentRows}<small>拒收 / 退货</small></b></div>
        {amounts && <>
          <div><span>本期应付</span><b className="tnum">{money(sum.billAmount)}</b></div>
          <div><span>已付</span><b className="tnum">{money(sum.cashAmount)}</b></div>
          <div><span>未付</span><b className={`tnum ${num(sum.cashBalanceAmount) > EPS ? s.owe : ''}`}>{money(Math.abs(num(sum.cashBalanceAmount)))}</b></div>
        </>}
      </div>}
      <article className={`${s.paper} ${customerScreen ? s.customerPaper : ''} ${customerPhone ? s.customerPhonePaper : ''} ${supplierTablet ? s.supplierTabletPaper : ''} ${supplierPhone ? s.supplierPhonePaper : ''}`} data-wm={wm}>
        {customerScreen && <div className={s.customerMeta}>{d.partner.code ? `${d.partner.code} · ` : ''}{termsText(d.partner)} · 日期口径：{basisLabel(d.dateBasis, scope)} · {Number(y)} 年 {Number(m)} 月</div>}
        <header className={s.title}>
          <h2>对账单{ff === 'phone' ? '（明细版）' : ''}</h2>
          <p>{customerPhone ? `${Number(y)} 年 ${Number(m)} 月 · ${(basisLabel(d.dateBasis, scope) === '客户下单日期' ? '客户下单日' : basisLabel(d.dateBasis, scope))}口径` : `${partnerLabel} · ${Number(y)} 年 ${Number(m)} 月 · ${periodReceiptBasis ? '实际入库日期' : basisLabel(d.dateBasis, scope)}口径`}</p>
        </header>
        <dl className={s.meta}>
          <div><dt>{partnerLabel}</dt><dd>{d.partner.name}</dd></div>
          <div><dt>对账单号</dt><dd className="tnum">{d.statementNo}</dd></div>
          <div><dt>对账期间</dt><dd className="tnum">{monthRange(d.period)}</dd></div>
          <div><dt>范围</dt><dd>{supplierPhone ? `${d.businessDocuments.length} 张采购单 · ${allGoodsCount} 个明细行` : customerPhone ? `${d.businessDocuments.length} 张订单 · ${allGoodsCount} 个明细行` : `${d.businessDocuments.length} 张单据`}</dd></div>
          {supplierPhone ? <div><dt>仓库</dt><dd>快照未记录</dd></div> : customerPhone ? null : <>
            <div><dt>结算方式</dt><dd>{termsText(d.partner)}</dd></div>
            <div><dt>状态</dt><dd>{statementStatus[d.status || 'DRAFT'].label}</dd></div>
          </>}
        </dl>

        <div className={s.grouped}>
          <div className={s.groupHead}>
            <b>货物明细</b>
            <span>{visibleDocuments.length} 张单据 · {goodsCount} 行货物{missingGoods ? ` · ${missingGoods} 张旧快照无货物行` : ''}</span>{receiptFilter}
            {customerScreen && bundleGroups.size > 0 && <div className={s.customerControls}>
              <button type="button" onClick={() => setKitComponentsOpen(true)} disabled={kitComponentsOpen}>全部展开套装</button>
              <button type="button" onClick={() => setKitComponentsOpen(false)} disabled={!kitComponentsOpen}>全部收起</button>
            </div>}
          </div>
          <div className={s.tableScroll}><table className={`${s.goodsTable} ${supplierGoods ? s.supplierTable : ''}`}>
            <thead>{supplierGoods
              ? <tr><th>货物</th><th>产品编号</th><th>包装方案</th><th className={s.right}>采购量</th><th className={s.right}>{periodReceiptBasis ? '本期入库/变动' : '已入库'}</th><th className={s.right}>下单单价</th><th className={s.right}>本单金额</th></tr>
              : <tr><th>货物 / 套装</th><th>产品编号</th><th className={s.right}>订购</th><th className={s.right}>已发</th><th className={s.right}>单价</th><th className={s.right}>金额</th><th>备注</th></tr>}</thead>
            <tbody>{visibleDocuments.map((doc) => {
              const st = documentStatus(doc.targetType, doc.status || doc.note);
              const tableGoods = !supplierGoods && !kitComponentsOpen
                ? doc.goods?.filter((good) => !(good.name.includes(' · ') && good.amount === 0))
                : doc.goods;
              return [
                <tr className={s.docHeader} key={`${doc.lineId || doc.id}-head`}><td colSpan={7}><b className="tnum">{doc.documentNo}</b><span>{doc.type} · {date(doc.documentDate)}{doc.dueDate ? ` · ${scope === 'supplier' ? '到货' : '交货'} ${date(doc.dueDate)}` : ''}</span>{st && <em>{st.label}</em>}</td></tr>,
                ...(tableGoods ? tableGoods.map((good, index) => <tr key={`${doc.lineId || doc.id}-good-${index}`}>
                  <td className={!supplierGoods ? s.customerGood : undefined}>{good.name}</td><td className="tnum">{good.code || '—'}</td>
                  {supplierGoods ? <>
                    <td>{good.packaging || '—'}</td>
                    <td className={`${s.right} tnum`}>{good.adjustmentKind ? '—' : good.orderedQuantity ?? good.quantity ?? '—'}</td>
                    <td className={`${s.right} tnum`}>{good.receivedQuantity ?? '—'}</td>
                    <td className={`${s.right} tnum`}>{good.unitPrice === undefined ? '—' : money(good.unitPrice)}</td>
                    <td className={`${s.right} tnum`}>{good.billingBasis === 'RECEIVED' && !good.receivedQuantity && !good.adjustmentKind ? <><b>不计本期</b><small className={s.receivedValue}>{periodReceiptBasis ? '后续入库归入实际月份' : '后续入库需核对'}</small></> : <><b className={good.adjustmentKind === 'REJECTION_CONTRA' || good.adjustmentKind === 'RETURN' || good.amount < 0 ? s.adjustmentAmount : undefined}>{money(good.amount)}</b>{good.billingBasis !== 'RECEIVED' && good.receivedAmount !== undefined && <small className={s.receivedValue}>历史入库参考 {money(good.receivedAmount)}</small>}</>}</td>
                  </> : <>
                    <td className={`${s.right} tnum`}>{good.quantity === undefined ? '—' : good.quantity}</td>
                    <td className={`${s.right} tnum`}>—<small className={s.receivedValue}>快照未记录</small></td>
                    <td className={`${s.right} tnum`}>{good.unitPrice === undefined ? '—' : money(good.unitPrice)}</td>
                    <td className={`${s.right} tnum`}>{money(good.amount)}</td>
                    <td>{good.adjustmentKind ? good.adjustmentKind === 'RETURN' ? '退货冲减' : '拒收冲减' : good.amount < 0 ? '金额差额' : good.amount === 0 && good.unitPrice === 0 ? '套装组成' : '—'}</td>
                  </>}
                </tr>) : [<tr key={`${doc.lineId || doc.id}-missing`}><td colSpan={7} className={s.oldSnapshot}>历史快照仅保存单据金额，没有货物行；不从实时订单补造。</td></tr>]),
                <tr className={s.subtotal} key={`${doc.lineId || doc.id}-subtotal`}><td colSpan={6}>{doc.documentNo} 小计</td><td className={`${s.right} tnum`}>{money(doc.amount)}</td></tr>,
              ];
            })}</tbody>
          </table></div>
          <div className={s.groupTotal}><span>{visibleDocuments.length} 张单据 · {goodsCount} 行货物{receiptBasis ? ' · 按已入库计价' : ''}</span><b className="tnum">{supplierGoods ? '本期采购应付' : '本期合计'} {money(t.amount)}</b></div>
        </div>

        {customerScreen && <section className={s.customerRules} aria-label="计价口径">
          <header><h3>计价口径</h3><span>历史快照缺少的明细不从当前订单补回</span></header>
          <div className={s.customerRuleRows}>
            <div><b>计价基准</b><span>按生成对账单时保存的金额固化；已发数量未记录，无法拆分未发部分。</span></div>
            <div><b>破损冲减</b><span>快照差额单独列示；未保存异常编号或原因时不回填。</span></div>
            <div><b>套装</b><span>按套计价，组件只作明细展示，不重复计价。</span></div>
            <div><b>运费</b><span>按实际费用单独列行；本历史快照未单独记录。</span></div>
            <div><b>退货</b><span>按已退货入库记录冲减；旧快照没有退货单号时不回填。</span></div>
          </div>
        </section>}

        {customerScreen && <aside className={s.customerSideCards} aria-label="明细版说明">
          <section>
            <h3>两种版式</h3>
            <div className={s.customerFormat}><b>汇总版</b><span>9 列单据级，用于财务内部核数。</span></div>
            <div className={`${s.customerFormat} ${s.customerFormatActive}`}><b>明细版</b><span>按订单分组，列出货物名称、编号、数量、单价和金额，给客户签字确认。</span></div>
          </section>
          <section className={s.customerKpis} aria-label="快照统计">
            <h3>快照统计</h3>
            <div><span>订单数</span><b className="tnum">{d.businessDocuments.length}</b></div>
            <div><span>明细行</span><b className="tnum">{goodsCount}</b></div>
            <div><span>其中套装</span><b className="tnum">{bundleGroups.size} 组</b></div>
            <div><span>冲减行</span><b className="tnum">{adjustmentRows}</b></div>
            <div><span>货物金额</span><b className="tnum">{money(goodsAmount)}</b></div>
            <div><span>运费</span><b className={s.unavailable}>未单独记录</b></div>
            <div><span>合计</span><b className="tnum">{money(t.amount)}</b></div>
          </section>
          <section>
            <h3>数据来源</h3>
            <p>货物明细在生成对账单时保存为快照，不实时读取订单。旧快照缺少的已发数量、运费拆分或异常编号会明确显示未记录。<br/><br/><b>账单金额与明细加总一致，</b>已锁账后按快照留存。</p>
          </section>
        </aside>}

        <div className={s.mobileDocs}>{receiptFilter}{visibleDocuments.map((doc) => {
          const st = documentStatus(doc.targetType, doc.status || doc.note);
          const inv = documentInvoice(doc, scope);
          return (
            <section key={doc.lineId || doc.id} className={s.doc}>
              <div className={s.dh}>
                <b className="tnum">{doc.documentNo}</b>
                {st && <span className={s[`st_${st.tone}`] || s.st_none}>{st.label}</span>}
              </div>
              <p className={s.dm}>
                {doc.type} · 单据日期 <span className="tnum">{date(doc.documentDate)}</span>
                {doc.dueDate && <> · {scope === 'supplier' ? '到货' : '交货'} <span className="tnum">{date(doc.dueDate)}</span></>}
              </p>
              {amounts && (
                <>
                  {ff !== 'phone' && <div className={s.ln}><span>单据金额</span><b className="tnum">{money(doc.amount)}</b></div>}
                  {showGoods && doc.goods && <div className={s.goods}>{doc.goods.map((good, index) => {
                    const deduction = good.adjustmentKind === 'REJECTION_CONTRA' || good.adjustmentKind === 'RETURN' || good.amount < 0;
                    const customerBundleParts = good.name.split(' · ');
                    const customerComponent = customerPhone && good.amount === 0 && customerBundleParts.length > 1;
                    const customerBundleParent = customerPhone && good.amount !== 0 && customerBundleParts.length > 1;
                    const customerName = customerComponent ? customerBundleParts.slice(1).join(' · ') : customerBundleParent ? customerBundleParts[0] : good.name;
                    const supplierDetail = good.adjustmentKind === 'RETURN'
                      ? `退货冲减 · 数量 ${good.receivedQuantity ?? '—'} × ${money(good.unitPrice)}`
                      : good.adjustmentKind === 'REJECTION_CONTRA'
                        ? `拒收冲减 · 数量 ${good.receivedQuantity ?? '—'} × ${money(good.unitPrice)}`
                        : good.adjustmentKind === 'REJECTION_GROSS'
                          ? `拒收相关入库 · 数量 ${good.receivedQuantity ?? good.quantity ?? '—'} × ${money(good.unitPrice)}`
                          : `采购 ${good.orderedQuantity ?? good.quantity ?? '—'} · 已入库 ${good.receivedQuantity ?? '—'} × ${money(good.unitPrice)}`;
                    return <div className={deduction ? s.adjustmentLine : undefined} key={`${good.code || good.name}-${index}`}>
                      {supplierGoods ? <>
                        <span>{good.name}{good.code && <small>{good.code}</small>}</span>
                        <b className={`tnum ${deduction ? s.adjustmentAmount : ''}`}>{good.billingBasis === 'RECEIVED' && !good.receivedQuantity && !good.adjustmentKind ? '不计本期' : money(good.amount)}{good.billingBasis !== 'RECEIVED' && good.receivedAmount !== undefined && <small>历史入库参考 {money(good.receivedAmount)}</small>}</b>
                        <small className={deduction ? s.adjustmentText : undefined}>{supplierDetail}</small>
                        {good.packaging && <small className={s.receivedValue}>包装方案 {good.packaging}</small>}
                      </> : customerPhone ? <>
                        <span className={customerComponent ? s.customerComponentName : undefined}>{customerName}{good.code && !customerBundleParent && <small>{good.code}</small>}</span>
                        <b className={`tnum ${deduction ? s.adjustmentAmount : ''} ${customerComponent ? s.customerComponentQty : ''}`}>{customerComponent ? `${good.quantity ?? '—'}` : money(good.amount)}</b>
                        {!customerComponent && <small className={deduction ? s.adjustmentText : undefined}>{good.quantity !== undefined ? `${good.quantity} × ${money(good.unitPrice)} · 已发数量快照未记录` : good.name.includes('运费') ? '实际运费' : '数量快照未记录'}</small>}
                        {customerComponent && <small className={s.customerComponentMeta}>套装组成 · {good.quantity ?? '—'} 件</small>}
                      </> : <><span>{good.code && <small>{good.code} · </small>}{good.name}{good.quantity !== undefined && <small> · {good.quantity} × {money(good.unitPrice)}</small>}</span><b className={`tnum ${deduction ? s.adjustmentAmount : ''}`}>{money(good.amount)}{good.receivedAmount !== undefined && <small>历史入库参考 {money(good.receivedAmount)}</small>}</b></>}
                    </div>;
                  })}</div>}
                  {supplierPhone && <div className={s.supplierMobileSubtotal}><span>{doc.documentNo} 小计</span><b className="tnum">{money(doc.amount)}</b></div>}
                  {ff !== 'phone' && <>
                    <div className={s.ln2}>
                      <span>已核销 <span className="tnum">{money(doc.cashAllocated)}</span></span>
                      <span className={num(doc.cashBalance) > EPS ? s.owe : undefined}>未核销 <span className="tnum">{money(doc.cashBalance)}</span></span>
                    </div>
                    <div className={s.ln2}>
                      <span>{inv.label}</span>
                      {doc.invoiceRequired && (
                        <span className={num(doc.invoiceBalance) > EPS ? s.warn : undefined}>
                          已核票 <span className="tnum">{money(doc.invoiceAllocated)}</span> · 待核票 <span className="tnum">{money(doc.invoiceBalance)}</span>
                        </span>
                      )}
                    </div>
                  </>}
                </>
              )}
            </section>
          );
        })}</div>
        {!d.businessDocuments.length && <p className={s.none}>这份对账单没有纳入业务单据。</p>}
        {missingGoods > 0 && <p className={s.oldNote}>{missingGoods} 张历史快照没有固化货物行，明细版只能展示原单据金额；重新取数仅适用于草稿。</p>}

        {amounts && (
          <>
            <div className={s.total}>
              <span>{supplierGoods ? '本期应付' : '本期合计'}</span>
              <b className="tnum">{money(t.amount)}</b>
            </div>
            {supplierPhone && <div className={s.supplierMobilePaid}><span>已付 <b className="tnum">{money(sum.cashAmount)}</b></span><span>未付 <b className={`tnum ${num(sum.cashBalanceAmount) > EPS ? s.owe : ''}`}>{money(Math.abs(num(sum.cashBalanceAmount)))}</b></span></div>}
            {customerPhone && <div className={s.customerPhoneAmounts}>
              <span>货物 <b className="tnum">{money(customerPhoneGoodsAmount)}</b></span>
              <em>+</em>
              <span>运费 <b className="tnum">{freightAmount ? money(freightAmount) : '快照未单独记录'}</b></span>
            </div>}
            {!supplierPhone && !customerPhone && !customerScreen && <div className={s.eqs}>
              <div className={s.eq}>
                <span><i>{sum.billLabel}</i><b className="tnum">{money(sum.billAmount)}</b></span>
                <em>−</em>
                <span><i>{sum.cashLabel}</i><b className="tnum">{money(sum.cashAmount)}</b></span>
                <em>=</em>
                <span className={num(sum.cashBalanceAmount) > EPS ? s.owe : undefined}><i>{sum.cashBalanceLabel}</i><b className="tnum">{money(Math.abs(num(sum.cashBalanceAmount)))}</b></span>
              </div>
              <div className={s.eq}>
                <span><i>{sum.invoiceRequiredLabel}</i><b className="tnum">{money(sum.invoiceRequiredAmount)}</b></span>
                <em>−</em>
                <span><i>{sum.invoiceAmountLabel}</i><b className="tnum">{money(sum.invoiceAmount)}</b></span>
                <em>=</em>
                <span className={num(sum.invoiceBalanceAmount) > EPS ? s.warn : undefined}><i>{sum.invoiceBalanceLabel}</i><b className="tnum">{money(Math.abs(num(sum.invoiceBalanceAmount)))}</b></span>
              </div>
            </div>}
          </>
        )}

        <section className={s.rules}>
          <h3>口径说明</h3>
          <ol>
            {supplierGoods && receiptBasis ? <>
              <li>按已入库数量计价，未到货列出但不计金额。</li>
              <li>到货当场拒收的冲减本期，单独成行注明原因。</li>
              <li>按包装方案下单（整箱整捆），单价挂在方案 + 供应商上。</li>
              <li>已下的采购单按下单当时的报价结算。</li>
              <li>入库后退回的冲减，标注退货单号。</li>
            </> : customerScreen || customerPhone ? <>
              <li>本历史快照按保存时金额固化；快照未记录已发数量，无法拆分未发部分。</li>
              <li>破损 / 补耗等差额单独列示；历史快照没有异常编号或原因时不回填。</li>
              <li>套装按套计价，组件仅作明细展示。</li>
              <li>运费按实际费用单独列行；快照未单独记录时标示为未记录。</li>
              <li>已退货入库的部分冲减以退货记录为准；旧快照未留退货单号时不回填。</li>
            </> : <>
              <li>{periodReceiptBasis ? `采购单按实际入库、拒收及退货流水发生日期归入 ${monthRange(d.period)}；本期未到货的采购单只列示货物，不计应付。` : `${scope === 'supplier' ? `采购单按「${basisLabel(d.dateBasis, scope)}」` : `业务订单按「${basisLabel(d.dateBasis, scope)}」、淘宝店订单按订单日期`}落在 ${monthRange(d.period)} 之间的纳入本期。`}</li>
              <li>单据金额、已核销、已核票都是保存对账单时的快照，之后单据再变动不影响本单。{supplierGoods && (receiptBasis ? periodReceiptBasis ? '本单应付按本月实际入库流水的数量和当时单价逐笔分币四舍五入后合计。' : '本历史快照按保存时累计已入库量计算。' : '历史对账单保持保存时金额口径，不从实时采购单改写。')}</li>
              <li>「未核销」「待核票」是逐单分摊后的余额；汇总里的{sum.cashLabel}、{sum.invoiceAmountLabel}按本月已登记的记录合计，多收或多开会在汇总里单独显示。</li>
              <li>标为「{scope === 'supplier' ? '无需收票' : '无需开票'}」的单据不计入{sum.invoiceRequiredLabel}。</li>
            </>}
          </ol>
        </section>

        <div className={s.sign}>
          <div><span>{partnerLabel}确认（签章）</span><i /></div>
          <div><span>日期</span><i /></div>
        </div>
        <footer className={s.pf}>
          <span className="tnum">{d.statementNo}</span>
          <span>{d.status === 'LOCKED' && d.lockedAt ? `锁账于 ${dateTime(d.lockedAt)}` : `生成于 ${dateTime(d.createdAt)}`}</span>
        </footer>
      </article>
    </div>
  );
}
